// Import Swiper styles
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "../../../theme/SwiperOverrides.css";

// import Swiper core and required modules
import { A11y, Navigation, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import React, { useState } from "react";
import SimpleSignPostCarouselCard from "../../../components/SignPostCards/SimpleSignPostCarouselCard/SimpleSignPostCarouselCard";
import { Button } from "../../../components/common";
import { ArrowCircle } from "../../../components/common/Icons";
import ProcessedImage from "../../../components/common/ProcessedImage/ProcessedImage";
import { useClientSide } from "../../../hooks/useClientSide";
import useWindowWidth from "../../../hooks/useWindowWidth";
import { Colours } from "../../../utils/colours";
import WrappedSection from "../../WrappedSection/WrappedSection";
import * as styles from "./ParagraphCaseStudy.module.scss";

export interface ParagraphCaseStudyProps {
	heading: string;
	subheading?: string;
	description?: string;
	buttonText?: string;
	buttonUrl?: string;
	imgSrc: string;
	imgAlt: string;
	stories: DogsTrust.SignPostCardProps[];
}

export const getParagraphCaseStudyProps = ({
	field_heading,
	field_sub_heading,
	field_description,
	field_link,
	relationships: {
		field_featured_image: { field_media_image },
		field_case_study_story,
	},
}: Queries.ParagraphCaseStudyDataFragment): ParagraphCaseStudyProps => ({
	heading: field_heading,
	subheading: field_sub_heading,
	description: field_description,
	buttonText: field_link?.title,
	buttonUrl: field_link?.uri,
	imgSrc: field_media_image?.uri,
	imgAlt: field_media_image?.alt,
	stories: field_case_study_story.map(
		(story) =>
			({
				title: story.title,
				buttonLabel: story.field_short_link_text,
				buttonUrl: story.path.alias,
				imgSrc:
					story.relationships?.field_featured_image?.field_media_image?.uri ||
					"",
				imgAlt:
					story.relationships?.field_featured_image?.field_media_image?.alt,
			}) as DogsTrust.SignPostCardProps,
	),
});
export const ParagraphCaseStudy: React.FC<ParagraphCaseStudyProps> = ({
	heading,
	subheading,
	description,
	buttonText,
	buttonUrl,
	imgSrc,
	stories,
}) => {
	const isClientSide = useClientSide();
	const windowWidth = useWindowWidth();
	const [swiper, setSwiper] = useState(null);
	const [currentSlide, setCurrentSlide] = useState(0);

	const prevSlide = () => swiper?.activeIndex > 0 && swiper?.slidePrev();
	const nextSlide = () =>
		swiper?.activeIndex < stories.length - 1 && swiper?.slideNext();

	const getVisibleCardCount = (wW: number) => {
		if (wW >= 1200) return 4;
		if (wW >= 1024) return 3;
		if (wW >= 750) return 2;
		if (wW >= 620) return 2;
		return 1;
	};
	return (
		<WrappedSection
			sectionName="ParagraphCaseStudy"
			sectionColour={Colours.SANDY_FUR}
		>
			<article className={styles.paragraphcasestudycontainer}>
				<div className={styles.image}>
					<ProcessedImage
						src={imgSrc}
						dimensions="600x450"
						width={545}
						height={409}
						alt=""
						aria-hidden
						loading={"lazy"}
					/>
				</div>
				<div className={styles.details}>
					<h3 className={styles.title}>{heading}</h3>
					{subheading && <h4 className={styles.subheading}>{subheading}</h4>}
					{description && <p className={styles.text}>{description}</p>}
					{buttonUrl && buttonText && (
						<div className={styles.buttonwrapper}>
							<Button
								variant={"secondary"}
								url={buttonUrl}
								fullWidth={windowWidth < 768}
							>
								{buttonText}
							</Button>
						</div>
					)}
				</div>
			</article>
			{stories.length > 0 && (
				<div className={styles.signpostcardcarouselinner}>
					<div className={styles.swiperwrapper}>
						{isClientSide && (
							<Swiper
								key={stories.length}
								className={styles.swiper}
								modules={[Virtual, Navigation, A11y]}
								edgeSwipeThreshold={10}
								threshold={0}
								grabCursor={true}
								draggable={false}
								resistance={false}
								autoplay={false}
								noSwiping
								allowTouchMove={false}
								loop
								onSwiper={(swiper) => setSwiper(swiper)}
								onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
								initialSlide={0}
								centerInsufficientSlides={true}
								breakpoints={{
									320: {
										slidesPerView: getVisibleCardCount(320),
										enabled: stories.length > getVisibleCardCount(320),
									},
									620: {
										slidesPerView: getVisibleCardCount(620),
										enabled: stories.length > getVisibleCardCount(620),
									},
									750: {
										slidesPerView: getVisibleCardCount(750),
										enabled: stories.length > getVisibleCardCount(750),
									},
									1024: {
										slidesPerView: getVisibleCardCount(1024),
										enabled: stories.length > getVisibleCardCount(1024),
									},
									1200: {
										slidesPerView: getVisibleCardCount(1200),
										enabled: stories.length > getVisibleCardCount(1200),
									},
								}}
							>
								{stories.map((storyCard: DogsTrust.SignPostCardProps) => (
									<SwiperSlide key={storyCard.title}>
										<SimpleSignPostCarouselCard {...storyCard} />
									</SwiperSlide>
								))}
							</Swiper>
						)}
						<div className={styles.navbuttons}>
							<button
								type="button"
								aria-label="previous"
								className={styles.back}
								onClick={prevSlide}
								tabIndex={0}
								onKeyDown={(e) => {
									e.key === "Enter" && prevSlide();
								}}
							>
								{currentSlide > 0 && <ArrowCircle rotation={180} />}
							</button>
							<button
								type="button"
								aria-label="next"
								className={styles.next}
								onClick={nextSlide}
								onKeyDown={(e) => {
									e.key === "Enter" && prevSlide();
								}}
								tabIndex={0}
							>
								{stories.length > getVisibleCardCount(windowWidth) && (
									<ArrowCircle rotation={0} />
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</WrappedSection>
	);
};

export default ParagraphCaseStudy;
